import { AnyAction, createReducer } from '@reduxjs/toolkit';

import {
	ConfigurationDataSourceType,
	ConfigurationRow,
	ConfigurationsStoreState,
	MultipleDeleteParams,
} from '@Types/configurations.types';

import {
	changeDataSourceTypesFilter,
	changeSensorConfigurationPhysicalFilter,
	changeSensorConfigurationSearchValue,
	changeSensorTypesFilter,
	checkOrUncheckedRowsChange,
	createConfiguration,
	createConfigurationRejected,
	createConfigurationResolved,
	deleteConfiguration,
	deleteConfigurationRejected,
	deleteConfigurationResolved,
	deleteMultipleConfigurations,
	deleteMultipleConfigurationsRejected,
	deleteMultipleConfigurationsResolved,
	getConfigurationById,
	getConfigurationByIdRejected,
	getConfigurationByIdResolved,
	getConfigurationDetails,
	getConfigurationDetailsRejected,
	getConfigurationDetailsResolved,
	getConfigurations,
	getConfigurationsRejected,
	getConfigurationsResolved,
	headerCheckboxChange,
	headerCheckboxCheckedChange,
	paginationModelChange,
	resetConfigurationsStore,
	resetCreateConfigurationStatus,
	restoreConfigurationByIdStatus,
	rowCheckboxChange,
	rowSelectionModelChange,
	selectConfiguration,
	updateConfiguration,
	updateConfigurationRejected,
	updateConfigurationResolved,
} from '@Actions';
import { Configuration } from '@Types';
import { isNonDeletableConfiguration } from '@Utils';
import { SensorType } from '@innovyze/shared-utils';
import { STATUS_ENUM, StylovyzeTableRow } from '@innovyze/stylovyze';

export const initialState: ConfigurationsStoreState = {
	searchValue: '',
	physicalOrVirtual: 'all',
	sensorTypes: [],
	dataSourceTypes: [],
	initialized: false,
	createSensorStatus: STATUS_ENUM.IDLE,
	status: 'idle',
	errorCategory: '',
	errorMsg: '',
	list: [],
	selectedConfig: undefined,
	deletionInProgress: false,
	loadingConfigurationDetails: false,
	pagination: {
		total: 0,
		previous: 0,
		next: 0,
		current: 1,
		pages: 0,
	},
	checkOrUncheckedRows: [],
	headerCheckboxChecked: false,
	configurationById: {
		status: STATUS_ENUM.IDLE,
	},
	rowSelectionModel: [],
	paginationModel: {
		page: 0,
		pageSize: 10,
	},
};

interface Configurations {
	[x: string]: (
		state: ConfigurationsStoreState,
		action: AnyAction,
	) => ConfigurationsStoreState | undefined | void;
}

const reducer: Configurations = {
	[resetConfigurationsStore.toString()]: state => ({
		...state,
		status: 'idle',
	}),
	[resetCreateConfigurationStatus.toString()]: state => ({
		...state,
		createSensorStatus: STATUS_ENUM.IDLE,
	}),
	[createConfiguration.toString()]: state => ({
		...state,
		createSensorStatus: STATUS_ENUM.PENDING,
	}),
	[createConfigurationResolved.toString()]: state => ({
		...state,
		createSensorStatus: STATUS_ENUM.SUCCESS,
	}),
	[createConfigurationRejected.toString()]: (state, action) => ({
		...state,
		createSensorStatus: STATUS_ENUM.ERROR,
		errorCategory: action.payload.errorCategory ?? '',
		errorMsg: action.payload.errorMsg ?? '',
	}),
	[updateConfiguration.toString()]: state => ({
		...state,
		status: 'pending',
	}),
	[updateConfigurationResolved.toString()]: (state, action) => {
		const payload = action.payload as Configuration;
		state.status = 'completed';
		state.list = state.list.map(current => {
			return current.sensorId !== payload.sensorId ? current : payload;
		});
		state.selectedConfig = state.selectedConfig
			? payload
			: state.selectedConfig;
		state.configurationById.configuration = payload;
	},
	[updateConfigurationRejected.toString()]: state => ({
		...state,
		status: 'rejected',
	}),
	[selectConfiguration.toString()]: (state, action) => {
		if (!action.payload) {
			return undefined;
		}

		const configurationIndex = state.list.findIndex(
			({ sensorId }) => sensorId === action.payload.sensorId,
		);

		if (configurationIndex >= 0 && state.list[configurationIndex]) {
			state.selectedConfig = state.list[configurationIndex];
		}

		return undefined;
	},

	[getConfigurationDetails.toString()]: (state: ConfigurationsStoreState) => {
		state.loadingConfigurationDetails = true;
		return undefined;
	},
	[getConfigurationDetailsRejected.toString()]: (
		state: ConfigurationsStoreState,
	) => {
		state.loadingConfigurationDetails = false;
		return undefined;
	},
	[getConfigurationDetailsResolved.toString()]: (state, action) => {
		state.loadingConfigurationDetails = false;
		state.initialized = true;

		if (!action.payload) {
			return undefined;
		}

		const configurationIndex = state.list.findIndex(
			({ sensorId }) => sensorId === action.payload.sensorId,
		);

		const { tankCurveId } = action.payload;

		if (
			state.selectedConfig &&
			configurationIndex >= 0 &&
			state.list[configurationIndex]
		) {
			state.selectedConfig.tankCurveId = tankCurveId;
		} else if (!state.selectedConfig) {
			state.selectedConfig = action.payload;
		}

		if (state.selectedConfig && !state.selectedConfig?.dataSourceType)
			state.selectedConfig.dataSourceType = 'ODBC';

		return undefined;
	},
	[restoreConfigurationByIdStatus.toString()]: state => {
		state.configurationById = {
			status: STATUS_ENUM.IDLE,
		};
		return undefined;
	},
	[getConfigurationById.toString()]: state => {
		state.configurationById = {
			status: STATUS_ENUM.PENDING,
		};
		return undefined;
	},
	[getConfigurationByIdResolved.toString()]: (state, action) => {
		state.initialized = true;

		state.configurationById = {
			status: STATUS_ENUM.SUCCESS,
			configuration: action.payload.configuration,
		};

		return undefined;
	},
	[getConfigurationByIdRejected.toString()]: state => ({
		...state,
		initialized: true,
		configurationById: {
			status: STATUS_ENUM.ERROR,
		},
	}),
	[getConfigurations.toString()]: state => {
		state.status = 'pending';
	},
	[getConfigurationsResolved.toString()]: (state, action) => {
		const list = (action.payload.configurations as ConfigurationRow[]).map(
			row => {
				const selected = state.checkOrUncheckedRows
					.map(r => r.sensorId)
					.includes(row.sensorId)
					? !state.headerCheckboxChecked
					: state.headerCheckboxChecked;
				return { ...row, selected };
			},
		);
		const checkOrUncheckedRows = state.checkOrUncheckedRows.filter(r =>
			state.sensorTypes.length > 0
				? state.sensorTypes.includes(r.sensorType)
				: true,
		);
		return {
			...state,
			initialized: true,
			status: 'completed',
			list,
			checkOrUncheckedRows,
			pagination: action.payload.pagination,
		};
	},
	[getConfigurationsRejected.toString()]: state => ({
		...state,
		status: 'rejected',
		initialized: true,
	}),
	[changeSensorConfigurationSearchValue.toString()]: (state, action) => {
		state.searchValue = action.payload;
	},
	[changeSensorConfigurationPhysicalFilter.toString()]: (state, action) => {
		state.physicalOrVirtual = action.payload;
	},
	[changeSensorTypesFilter.toString()]: (state, action) => {
		state.sensorTypes = [...action.payload];
	},
	[changeDataSourceTypesFilter.toString()]: (state, action) => {
		state.dataSourceTypes = [...action.payload];
	},
	[deleteConfiguration.toString()]: state => {
		state.deletionInProgress = true;
	},
	[deleteConfigurationResolved.toString()]: (state, action) => {
		state.deletionInProgress = false;
		state.list = state.list.filter(
			el => el.sensorId !== action.payload.sensorId,
		);
	},
	[deleteConfigurationRejected.toString()]: state => {
		state.deletionInProgress = false;
	},
	[deleteMultipleConfigurations.toString()]: state => {
		state.deletionInProgress = true;
	},
	[deleteMultipleConfigurationsResolved.toString()]: (state, action) => {
		state.deletionInProgress = false;
		state.headerCheckboxChecked = false;
		state.checkOrUncheckedRows = [];
		const { onlyData } = action.payload as MultipleDeleteParams;
		if (!onlyData) {
			state.list = state?.list?.filter(
				element =>
					element.selected !== true ||
					isNonDeletableConfiguration(
						element.dataSourceType ?? element.sourceType,
					),
			);
		}
		state.list = state.list.map(r => {
			return {
				...r,
				selected: false,
			};
		});
	},
	[deleteMultipleConfigurationsRejected.toString()]: state => {
		state.deletionInProgress = false;
	},
	[headerCheckboxChange.toString()]: (state, action) => {
		const checked = action.payload as boolean;
		state.headerCheckboxChecked = checked;
		state.list = state.list.map(row => ({ ...row, selected: checked }));
		state.checkOrUncheckedRows = [];
	},
	[rowCheckboxChange.toString()]: (state, action) => {
		const { row, checked } = action.payload as {
			row: StylovyzeTableRow;
			checked: boolean;
		};
		state.list = state.list.map(r => {
			return {
				...r,
				selected: r.sensorId === row.key ? checked : r.selected,
			};
		});

		const selectedEntry = state.list.find(
			entry => entry.sensorId === row.key,
		);
		const dataSourceType =
			selectedEntry?.dataSourceType ??
			selectedEntry?.sourceType ??
			ConfigurationDataSourceType.ODBC;

		state.checkOrUncheckedRows = state.checkOrUncheckedRows.some(
			({ sensorId }) => sensorId === row.key.toString(),
		)
			? state.checkOrUncheckedRows.filter(
					({ sensorId }) => sensorId !== row.key.toString(),
			  )
			: [
					...state.checkOrUncheckedRows,
					{
						sensorId: row.key as string,
						sensorType: row.cells[2] as SensorType,
						dataSourceType: dataSourceType,
					},
			  ];
	},
	[rowSelectionModelChange.toString()]: (state, action) => {
		state.rowSelectionModel = action.payload;
	},
	[headerCheckboxCheckedChange.toString()]: (state, action) => {
		state.headerCheckboxChecked = action.payload;
	},
	[checkOrUncheckedRowsChange.toString()]: (state, action) => {
		state.checkOrUncheckedRows = action.payload;
	},
	[paginationModelChange.toString()]: (state, action) => {
		state.paginationModel = { ...state.paginationModel, ...action.payload };
	},
};

export default createReducer(initialState, reducer);
